import React from 'react';
import styled from 'styled-components';
import { DrillDownBreakdown } from '../../types/drilldown';
import { Colors, Spacing, FontSizes, BorderRadius, Shadows } from '../../constants/theme';

interface DrillDownBreakdownGridProps {
  breakdowns: DrillDownBreakdown[];
  onDrillDown: (breakdown: DrillDownBreakdown) => void;
  loading?: boolean;
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${Spacing.md}px;
  margin: ${Spacing.lg}px 0;
`;

const BreakdownCard = styled.div<{ canDrillDown: boolean }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.lg}px;
  cursor: ${props => props.canDrillDown ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    ${props => props.canDrillDown && `
      border-color: ${Colors.borderAccent};
      box-shadow: ${Shadows.md};
      transform: translateY(-2px);
    `}
  }

  ${props => props.canDrillDown && `
    &::after {
      content: '→';
      position: absolute;
      top: ${Spacing.sm}px;
      right: ${Spacing.sm}px;
      color: ${Colors.textTertiary};
      font-size: ${FontSizes.lg}px;
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover::after {
      opacity: 1;
    }
  `}
`;

const CategoryLabel = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.sm}px 0;
  line-height: 1.4;
`;

const ValueDisplay = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${Spacing.xs}px;
  margin-bottom: ${Spacing.sm}px;
`;

const MainValue = styled.span`
  font-size: ${FontSizes.xxxl}px;
  font-weight: 700;
  color: ${Colors.textPrimary};
`;

const ValueUnit = styled.span`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
`;

const MetricsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacing.md}px;
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const MetricLabel = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.span<{ trend?: 'up' | 'down' | 'stable' }>`
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  color: ${props => {
    if (props.trend === 'up') return Colors.success;
    if (props.trend === 'down') return Colors.danger;
    return Colors.textSecondary;
  }};
`;

const TrendIndicator = styled.span<{ trend: 'up' | 'down' | 'stable' }>`
  font-size: ${FontSizes.sm}px;
  color: ${props => {
    if (props.trend === 'up') return Colors.success;
    if (props.trend === 'down') return Colors.danger;
    return Colors.textTertiary;
  }};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${Colors.backgroundTertiary};
  border-radius: ${BorderRadius.xs}px;
  overflow: hidden;
  margin: ${Spacing.sm}px 0;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  width: ${props => Math.min(props.percentage, 100)}%;
  background: linear-gradient(90deg, ${Colors.secondary}, ${Colors.accent});
  border-radius: ${BorderRadius.xs}px;
  transition: width 0.3s ease;
`;

const LoadingSkeleton = styled.div`
  background: ${Colors.backgroundTertiary};
  border-radius: ${BorderRadius.xs}px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const SkeletonCard = styled(LoadingSkeleton)`
  height: 160px;
  border-radius: ${BorderRadius.md}px;
`;

export const DrillDownBreakdownGrid: React.FC<DrillDownBreakdownGridProps> = ({
  breakdowns,
  onDrillDown,
  loading = false,
}) => {
  if (loading) {
    return (
      <GridContainer>
        {Array.from({ length: 6 }, (_, i) => (
          <SkeletonCard key={i} />
        ))}
      </GridContainer>
    );
  }

  const formatValue = (value: number, percentage: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  const getTrendSymbol = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return '↗';
      case 'down': return '↘';
      case 'stable': return '→';
      default: return '→';
    }
  };

  return (
    <GridContainer>
      {breakdowns.map((breakdown, index) => (
        <BreakdownCard
          key={`${breakdown.category}-${index}`}
          canDrillDown={breakdown.canDrillDown}
          onClick={() => breakdown.canDrillDown && onDrillDown(breakdown)}
          role={breakdown.canDrillDown ? 'button' : undefined}
          tabIndex={breakdown.canDrillDown ? 0 : undefined}
          onKeyDown={(e) => {
            if (breakdown.canDrillDown && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onDrillDown(breakdown);
            }
          }}
        >
          <CategoryLabel>{breakdown.category}</CategoryLabel>
          
          <ValueDisplay>
            <MainValue>{formatValue(breakdown.value, breakdown.percentage)}</MainValue>
            <ValueUnit>({breakdown.percentage.toFixed(1)}%)</ValueUnit>
          </ValueDisplay>

          <ProgressBar>
            <ProgressFill percentage={breakdown.percentage} />
          </ProgressBar>

          <MetricsRow>
            <MetricItem>
              <MetricLabel>Count</MetricLabel>
              <MetricValue>{breakdown.count?.toLocaleString() || '—'}</MetricValue>
            </MetricItem>
            
            <MetricItem>
              <MetricLabel>Trend</MetricLabel>
              <MetricValue trend={breakdown.trend}>
                <TrendIndicator trend={breakdown.trend || 'stable'}>
                  {getTrendSymbol(breakdown.trend || 'stable')}
                </TrendIndicator>
              </MetricValue>
            </MetricItem>

            {breakdown.metadata?.confidence && (
              <MetricItem>
                <MetricLabel>Confidence</MetricLabel>
                <MetricValue>
                  {(breakdown.metadata.confidence * 100).toFixed(0)}%
                </MetricValue>
              </MetricItem>
            )}
          </MetricsRow>
        </BreakdownCard>
      ))}
    </GridContainer>
  );
};